// MA-492/Slice 4 (D7-D11 rev-1) — category-level TEXT hint templates for
// grammarItems + semanticRelations (1,520 items total, zero AI image spend).
//
// WHY TEXT, NOT IMAGES (D7/D8 rev-1, ADR-096): semanticRelations gets zero
// images (confirms MA-121 — category/function relationTypes have
// `targetWordOrConcept === correctAnswer` verbatim, a direct leak;
// part_whole/attribute leak via world knowledge, the same class MA-121
// already rejected for e.g. mast->ship). grammarItems gets zero images too
// (extends MA-457 — illustrating any candidate word either depicts the
// answer, if it's the answer, or points at the wrong tile, if it's a
// distractor; a generic category icon teaches nothing). See
// docs/adr/ADR-096-no-images-for-category-packs.md for the full verdict.
//
// WHY A CATEGORY-LEVEL TEST, NOT A PER-ITEM HINT (D9): each template states
// the CATEGORY'S DEFINING TEST the child can apply to every option — never
// which option passes it. This is the same kind of format-driven elimination
// strategy ADR-088 already accepts for controlledSentences ("the
// multiple-choice format itself lets a reader eliminate distractors...") —
// applying a stated test to 3-4 options and eliminating those that fail is
// the intended metalinguistic skill being assessed here, not a shortcut
// around it. Authored ONCE per skill/relationType (O(18)), not per item
// (O(1520)) — never names any option or the correct answer.
//
// D10 rev-1 REVIEW NOTE (content-review pass performed at authoring time,
// not deferred): `article`, `conjunction`, and `common_proper` were flagged
// for extra care.
//   - `article`: correctAnswer values are full two-word phrases ("a kite",
//     "an egg"), not bare articles — the review risk is the PHRASE-FINAL
//     NOUN being guessable by a word-association shortcut (an untested
//     question this design doesn't measure), not "too few possible
//     answers" (that original rationale was wrong, see the design spec).
//     The template below never mentions or hints at any phrase's noun.
//   - `common_proper`: reviewed directly against MA-443's casing-leak
//     precedent (docs/build/BACKLOG.md:4093-4103 — "a capital letter is a
//     100%-predictive non-grammar cue" in this codebase's own measured
//     content). The template deliberately does NOT say "look for the
//     capital letter" (that IS the leak MA-443 documents) — it teaches the
//     name-of-a-person/place test instead, which applies correctly
//     regardless of casing.
//   - `conjunction`: reviewed for the same phrase/word-association risk as
//     `article`; the template states the joining-function test only.
//
// LEAK SWEEP (mandatory, enforced by
// src/lib/engine/__tests__/categoryHintTemplateLeakSweep.test.mjs): every
// template below was verified, word-normalized, to contain neither the
// correctAnswer (word-boundary match, mirrors HintSlot's normalizeForEcho +
// readingHintContentAudit.mjs's senTextLeaksAnswer) nor a substring/superstring
// of the item's own prompt, over ALL 1,520 real launch_safe items in both
// packs. Two of D9's exact verbatim strings (synonym/antonym) were adjusted
// during that sweep: "Try each option in the sentence" collided with real
// antonym/synonym answers ("in", "see") via whole-word containment — the
// pedagogical intent (SAME/OPPOSITE test) is unchanged, only the closing
// clause was reworded to "Try each option and check which one fits." This
// is a content-authoring correction forced by the mandatory leak sweep
// (§00h: never weaken the guard to reach green), not a design deviation.

export type GrammarSkill =
  | "noun"
  | "verb"
  | "adjective"
  | "preposition"
  | "singular_plural"
  | "adverb"
  | "pronoun"
  | "article"
  | "conjunction"
  | "common_proper"
  | "past_present"
  | "morphology";

export type SemanticRelationType =
  | "synonym"
  | "antonym"
  | "category"
  | "function"
  | "part_whole"
  | "attribute";

// D9's table, verbatim for the 5 explicitly-given grammar skills + all 6
// semantic relationTypes (synonym/antonym reworded per the leak-sweep note
// above); the other 7 grammar skills authored against each skill's own
// `validationRule` field with the ANSWER-NAMING clause stripped, same
// pattern, one template each (D9).
export const GRAMMAR_HINT_TEMPLATES: Readonly<Record<GrammarSkill, string>> = Object.freeze({
  noun: "A naming word tells you a person, place, or thing. Try each word - does it name one of those?",
  verb: "An action word tells you what someone DOES. Try saying 'I can ___' before each word.",
  adjective: "A describing word tells you MORE about a naming word. Try saying '___ dog' before each word.",
  preposition: "A word like this tells you WHERE something is. Which word answers 'where'?",
  singular_plural: "Look for the word that means more than one.",
  adverb: "A word like this tells you HOW, WHEN, or WHERE something happens. Try saying 'She did it ___' before each word.",
  pronoun: "A pronoun stands in for a naming word instead of repeating it. Ask: could it swap in for a person's or thing's name?",
  article: "A short word like 'a', 'an', or 'the' comes right before a naming word, without saying which one. Which choice starts that way?",
  conjunction: "A joining word links two ideas together. Which word connects the two parts of the sentence?",
  common_proper: "A proper noun is the one special name for a particular person, place, or pet - not just any example of that kind. Ask: is this an actual name someone would be called, or just a general kind of thing?",
  past_present: "Verbs change form to show WHEN something happens. Ask: does this word describe something ALREADY finished, or something happening right now?",
  morphology: "A word part like a prefix or suffix changes a word's meaning. Break the word apart - what does each part add?",
});

export const SEMANTIC_RELATION_HINT_TEMPLATES: Readonly<Record<SemanticRelationType, string>> = Object.freeze({
  synonym: "Think of a word that means almost the SAME. Try each option and check which one fits.",
  antonym: "Think of a word that means the OPPOSITE. Try each option and check which one fits.",
  category: "Think about what KIND of thing it is.",
  function: "Think about what JOB this thing does.",
  part_whole: "Think about what this piece belongs to.",
  attribute: "Think about how it looks, feels, sounds, or acts.",
});

/** Fail-closed: an unrecognized skill returns undefined, never a generic
 *  filler string — the mapper must offer NO SEN hint for a skill this table
 *  doesn't cover, rather than a placeholder that could be a leak or a
 *  useless echo for a category it wasn't authored against. */
export function resolveGrammarHintCue(skill: string | undefined): string | undefined {
  if (!skill) return undefined;
  return GRAMMAR_HINT_TEMPLATES[skill as GrammarSkill];
}

/** Same fail-closed contract as resolveGrammarHintCue, for semanticRelations. */
export function resolveSemanticRelationHintCue(relationType: string | undefined): string | undefined {
  if (!relationType) return undefined;
  return SEMANTIC_RELATION_HINT_TEMPLATES[relationType as SemanticRelationType];
}
